import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useT } from '@/i18n';
import { ErrorBox, Field } from '@/components/ui';
import { api } from '@/lib/api/endpoints';
import { adoptSession } from '@/lib/prefs';
import { logError } from '@/lib/log';
import { AuthLayout } from './AuthLayout';

/**
 * /reset/:token — the one-time password link Castfeeds support issues from the admin console
 * (POST /admin/v1/users/{id}/password-reset). Saving signs the user in and ends their other sessions.
 */
export function ResetPasswordPage() {
  const { t } = useT();
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const differ = repeat.length > 0 && repeat !== password;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (differ || password.length < 10) return;
    setBusy(true);
    setError(null);
    try {
      adoptSession(await api.auth.resetPassword(token, password));
      navigate('/', { replace: true });
    } catch (err) {
      logError('auth', 'password reset failed', err);
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout title={t('resetTitle')} sub={t('resetSub')}>
      <form onSubmit={submit} className="stack" style={{ gap: 14 }}>
        {error ? <ErrorBox error={error} /> : null}
        <Field label={t('newPassword')} hint={t('passwordHint')}>{(p) => <input {...p} className="input" type="password" dir="ltr" autoComplete="new-password" minLength={10} required value={password} onChange={(e) => setPassword(e.target.value)} />}</Field>
        <Field label={t('confirmPassword')} error={differ ? t('passwordsDiffer') : undefined}>{(p) => <input {...p} className="input" type="password" dir="ltr" autoComplete="new-password" required value={repeat} onChange={(e) => setRepeat(e.target.value)} />}</Field>
        <button type="submit" className="btn btn-primary" disabled={busy || differ || password.length < 10 || repeat !== password}>{busy ? t('resetting') : t('resetBtn')}</button>
      </form>
    </AuthLayout>
  );
}
